import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Accordion from 'components/Luxkit/Accordion'
import Divider from 'components/Luxkit/Divider'
import LineCardAtmIcon from 'components/Luxkit/Icons/line/LineCardAtmIcon'
import LineFileInfoAltIcon from 'components/Luxkit/Icons/line/LineFileInfoAltIcon'
import LineHomeAltIcon from 'components/Luxkit/Icons/line/LineHomeAltIcon'
import LineKeySkeletonAltIcon from 'components/Luxkit/Icons/line/LineKeySkeletonAltIcon'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE } from 'constants/dateFormats'
import { HeaderHeightContext } from 'contexts/HeaderStateContext'
import { format24HourTime } from 'lib/datetime/timeUtils'
import { scrollToHash } from 'lib/window/scrollUtils'
import { rem } from 'polished'
import React, { useContext, useEffect } from 'react'
import { useLocation } from 'react-router'
import styled from 'styled-components'

const CheckInDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1px 1fr 0px;
  grid-gap: ${rem(24)};
  overflow: hidden;

  >:last-child {
    grid-column: span 4;
  }

  ${mediaQueryUp.tablet} {
    grid-template-columns: max-content 1px max-content 1px 1fr;

    >:last-child {
      grid-column: unset;
    }
  }
`

interface Props {
  offer: App.VillaOffer;
}

function VillasThingsToKnow(props: Props) {
  const { offer } = props
  const { heights } = useContext(HeaderHeightContext)

  const location = useLocation()

  useEffect(() => {
    scrollToHash(location.hash, true)
  }, [location])

  return (
    <Group direction="vertical" gap={32}>
      <Heading variant="heading2">Things to know</Heading>
      <CheckInDetails>
        <VerticalSpacer gap={4}>
          <Caption variant="large" colour="neutral-three">Check-in</Caption>
          <BodyText variant="large" weight="bold">
            {!offer.property.checkInTo && `After ${format24HourTime(offer.property.checkInFrom, SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE)}`}
            {!!offer.property.checkInTo && `Between ${format24HourTime(offer.property.checkInFrom, SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE)} and ${format24HourTime(offer.property.checkInTo, SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE)}`}
          </BodyText>
        </VerticalSpacer>
        <Divider direction="vertical" kind="primary" />
        <VerticalSpacer gap={4}>
          <Caption variant="large" colour="neutral-three">Check-out</Caption>
          <BodyText variant="large" weight="bold">
            Before {format24HourTime(offer.property.checkOutUntil, SHORT_TIME_HOUR_FROMAT_AM_PM_SPACE)}
          </BodyText>
        </VerticalSpacer>
        <Divider direction="vertical" kind="primary" />
        <VerticalSpacer gap={4}>
          <BodyTextBlock variant="medium" weight="bold" startIcon={<LineKeySkeletonAltIcon />}>
            Meet & greet check-in
          </BodyTextBlock>
          <BodyText variant="medium">
            At check-in, you'll be greeted by the staff, ensuring a stress-free arrival.
          </BodyText>
        </VerticalSpacer>
      </CheckInDetails>
      <VerticalSpacer gap={20}>
        <Heading variant="heading5">
          Important Info
        </Heading>
        <Group direction="vertical" noDisplayWhenEmpty>
          { offer.property.copy.houseRules && <Accordion
            startIcon={<LineHomeAltIcon />}
            heading="House rules"
            scrollOnOpen
            scrollMarginTop={heights.siteStickyBanner}
          >
            <MarkdownRender content={offer.property.copy.houseRules} fontSize="medium" />
          </Accordion>}
          {offer.property.copy.paymentDetails && <Accordion
            startIcon={<LineCardAtmIcon />}
            heading="Security Deposit"
            scrollOnOpen
            scrollMarginTop={heights.siteStickyBanner}
          >
            <MarkdownRender content={offer.property.copy.paymentDetails} fontSize="medium" />
          </Accordion>}
          {offer.property.copy.finePrint && <Accordion
            startIcon={<LineFileInfoAltIcon />}
            id="fine-print"
            heading="Fine print"
            scrollMarginTop={heights.siteStickyBanner}
            scrollOnOpen
            openOnHash
          >
            <MarkdownRender content={offer.property.copy.finePrint} fontSize="medium" />
          </Accordion>}
        </Group>
      </VerticalSpacer>
    </Group>
  )
}

export default VillasThingsToKnow
